/**
 * Прежняя фамилия должника по документам (сверка «Айнбиндер», 26.10.2026:
 * обязательная графа формы № 530 оставалась пустой, хотя в справке МВД,
 * кредитном отчёте и ЕГРН должница значится как Журавлева).
 *
 * Правило: ФИО из документа, у которого имя и отчество совпадают с делом,
 * а фамилия другая, — это прежняя фамилия. Так оно работает при любом
 * порядке в документе: бюро, не знающее о смене фамилии, пишет прежнюю
 * в шапке, а нынешнюю — в «Предыдущих ФИО». Справка МВД пишет
 * «АЙНБИНДЕР (ЖУРАВЛЕВА) ЛИДИЯ АЛЕКСЕЕВНА» — скобки разбираются.
 *
 * В графу идёт только фамилия (решение заказчика 26.10.2026).
 */

const key = (value: string) => value.trim().toLowerCase().replace(/ё/g, 'е');

/** «ЖУРАВЛЕВА» → «Журавлева»; двойная фамилия — по частям. */
const titleCase = (value: string) =>
  value
    .toLowerCase()
    .split('-')
    .map((part) => (part ? part[0]!.toUpperCase() + part.slice(1) : part))
    .join('-');

export function previousSurnames(
  names: string[],
  debtor: { last: string; first: string; middle?: string; previousNames?: string[] },
): string[] {
  const known = new Set([key(debtor.last), ...(debtor.previousNames ?? []).map((n) => key(n.split(/\s+/)[0] ?? ''))]);
  const found: string[] = [];
  for (const name of names) {
    // «АЙНБИНДЕР (ЖУРАВЛЕВА) ЛИДИЯ АЛЕКСЕЕВНА» → фамилии [АЙНБИНДЕР, ЖУРАВЛЕВА]
    const match = /^\s*([А-ЯЁа-яё-]+)(?:\s*\(([^)]+)\))?\s+([А-ЯЁа-яё-]+)\s+([А-ЯЁа-яё-]+)/.exec(name);
    if (!match) continue;
    const [, last, inBrackets, first, middle] = match;
    if (key(first!) !== key(debtor.first) || key(middle!) !== key(debtor.middle ?? '')) continue;
    for (const surname of [last!, ...(inBrackets ?? '').split(/[,;]/)]) {
      const clean = surname.trim();
      if (clean && !known.has(key(clean))) {
        known.add(key(clean));
        found.push(titleCase(clean));
      }
    }
  }
  return found;
}

/**
 * ФИО людей в тексте документа: три слова с заглавной, последнее — отчество.
 * Окончание отчества отсекает адреса, названия банков и прочие сочетания.
 */
export function personNamesIn(text: string): string[] {
  const pattern = /([А-ЯЁ][а-яё]+(?:-[А-ЯЁ][а-яё]+)?)\s+([А-ЯЁ][а-яё]+)\s+([А-ЯЁ][а-яё]+(?:вна|вич|чна|ична|ич|кызы|оглы))(?![а-яё])/g;
  const names = new Set<string>();
  for (const m of text.matchAll(pattern)) names.add(`${m[1]} ${m[2]} ${m[3]}`);
  return [...names];
}
